/**
 * NMEA 0183, the wire format a real GPS speaks.
 *
 * This is the boundary of the whole position pipeline. The simulator emits
 * these exact sentences, a real receiver over WiFi or SignalK would emit the
 * same, and the app parses them back into a normalised fix. Build the seam
 * here in the real format and swapping in hardware later changes nothing
 * downstream.
 *
 * We handle the two sentences that matter for a position feed:
 *   - RMC, the "recommended minimum", position, SOG, COG, UTC date and time.
 *   - GGA, the fix-quality sentence, position, fix type, satellites, HDOP.
 *
 * RMC carries everything we need to drive own ship; GGA adds the quality
 * fields we want for a signal-health readout. Position in NMEA is degrees and
 * decimal minutes (ddmm.mmmm), which is also how we read positions everywhere
 * else, so no awkward conversions creep in.
 */

/** A normalised GNSS fix, in the app's own [lon, lat] convention. */
export interface GnssFix {
  /** [lon, lat] WGS84 degrees. */
  position: [number, number];
  /** Speed over ground, knots. */
  sogKn?: number;
  /** Course over ground, degrees true. */
  cogDeg?: number;
  /** Fix timestamp (UTC). */
  utc?: Date;
  /** GGA fix quality: 0 = no fix, 1 = GPS, 2 = DGPS. */
  fixQuality?: number;
  /** Satellites in use. */
  satellites?: number;
  /** Horizontal dilution of precision. */
  hdop?: number;
  /**
   * Estimated horizontal position error, metres (1-sigma, i.e. the "±X m" a
   * receiver reports). Comes straight from a GST sentence when present, else
   * estimated from HDOP.
   */
  horizontalErrorM?: number;
}

/** A parsed RMC sentence. */
export interface RmcSentence {
  type: 'RMC';
  /** A = valid (active), V = void. We only trust a fix when valid. */
  valid: boolean;
  position: [number, number] | null;
  sogKn: number | null;
  cogDeg: number | null;
  utc: Date | null;
}

/** A parsed GGA sentence. */
export interface GgaSentence {
  type: 'GGA';
  position: [number, number] | null;
  fixQuality: number;
  /** Satellites in use, or undefined when the sentence leaves the field empty
   *  (a receiver that doesn't report a count). Never coerced to 0. */
  satellites: number | undefined;
  hdop: number;
}

/** A parsed GST sentence (position-error statistics). */
export interface GstSentence {
  type: 'GST';
  /** RMS value of the standard deviation of the range inputs, metres. */
  rmsM: number;
  /** Error ellipse semi-major axis, metres. */
  semiMajorM: number;
  /** Error ellipse semi-minor axis, metres. */
  semiMinorM: number;
  /** Orientation of the semi-major axis, degrees true. */
  orientDeg: number;
  /** Standard deviation of latitude error, metres. */
  latErrM: number;
  /** Standard deviation of longitude error, metres. */
  lonErrM: number;
  /** Standard deviation of altitude error, metres. */
  altErrM: number;
}

/** A parsed DPT sentence (depth of water below the transducer + offset). */
export interface DptSentence {
  type: 'DPT';
  /** Depth below the transducer, metres. */
  depthM: number;
  /** Transducer offset, metres: +ve = distance to waterline, -ve = to keel. */
  offsetM: number | null;
}

/** A parsed DBT sentence (depth below transducer, in feet/metres/fathoms). */
export interface DbtSentence {
  type: 'DBT';
  /** Depth below the transducer, metres (we read the metres field). */
  depthM: number;
}

/** A parsed VHW sentence (water speed and heading). */
export interface VhwSentence {
  type: 'VHW';
  /** Heading, degrees true. Null if the talker only gave a magnetic heading. */
  headingTrueDeg: number | null;
  /** Speed through the water, knots. */
  speedThroughWaterKn: number;
}

/** A parsed MWV sentence (wind speed and angle, apparent or true). */
export interface MwvSentence {
  type: 'MWV';
  /** Wind angle relative to the bow, degrees 0..360 (0 = dead ahead, +clockwise to starboard). */
  windAngleDeg: number;
  /** 'R' = relative (apparent) wind, 'T' = theoretical (true) wind. */
  reference: 'R' | 'T';
  /** Wind speed, knots. */
  windSpeedKn: number;
  /** Data-valid flag (status A). */
  valid: boolean;
}

/** A parsed HDG/HDT heading sentence, reduced to a true heading. */
export interface HeadingSentence {
  type: 'HDG' | 'HDT';
  /** Vessel heading, degrees true (variation/deviation applied for HDG when present). */
  headingTrueDeg: number;
  /** Magnetic heading if the sentence carried one (HDG), else null. */
  headingMagneticDeg: number | null;
}

/** A parsed MWD sentence (wind direction and speed, true). */
export interface MwdSentence {
  type: 'MWD';
  /** Direction the wind comes FROM, degrees true. */
  windFromDeg: number;
  /** Wind speed, knots. */
  windSpeedKn: number;
}

export type NmeaSentence =
  | RmcSentence
  | GgaSentence
  | GstSentence
  | DptSentence
  | DbtSentence
  | VhwSentence
  | MwvSentence
  | HeadingSentence
  | MwdSentence;

// -----------------------------------------------------------------------------
// Checksum
// -----------------------------------------------------------------------------

/**
 * NMEA checksum: XOR of every character between the leading `$` and the `*`,
 * rendered as two uppercase hex digits. Pass the body only (no `$`, no `*CS`).
 */
export function nmeaChecksum(body: string): string {
  let cs = 0;
  for (let i = 0; i < body.length; i++) cs ^= body.charCodeAt(i);
  return cs.toString(16).toUpperCase().padStart(2, '0');
}

/** Wrap a sentence body as `$<body>*<CS>` with a correct checksum. */
function frame(body: string): string {
  return `$${body}*${nmeaChecksum(body)}`;
}

// -----------------------------------------------------------------------------
// Coordinate formatting (degrees + decimal minutes, NMEA style)
// -----------------------------------------------------------------------------

function encodeLat(lat: number): { field: string; hemi: 'N' | 'S' } {
  const hemi = lat >= 0 ? 'N' : 'S';
  const abs = Math.abs(lat);
  const deg = Math.trunc(abs);
  const min = (abs - deg) * 60;
  // ddmm.mmmm, degrees padded to 2, minutes to 2 integer digits + 4 decimals.
  const field = `${String(deg).padStart(2, '0')}${min.toFixed(4).padStart(7, '0')}`;
  return { field, hemi };
}

function encodeLon(lon: number): { field: string; hemi: 'E' | 'W' } {
  const hemi = lon >= 0 ? 'E' : 'W';
  const abs = Math.abs(lon);
  const deg = Math.trunc(abs);
  const min = (abs - deg) * 60;
  // dddmm.mmmm, longitude degrees padded to 3.
  const field = `${String(deg).padStart(3, '0')}${min.toFixed(4).padStart(7, '0')}`;
  return { field, hemi };
}

/**
 * Parse an NMEA ddmm.mmmm coordinate field. `degDigits` is 2 for latitude and
 * 3 for longitude (the rest of the field is the minutes). Returns signed
 * decimal degrees, or null if the field is empty or malformed.
 */
function parseCoord(field: string, hemi: string, degDigits: number): number | null {
  if (!field) return null;
  const dot = field.indexOf('.');
  // The minutes part is always the two digits before the dot plus the
  // decimals; everything before that is whole degrees.
  const intLen = dot === -1 ? field.length : dot;
  const degLen = intLen - 2;
  if (degLen < degDigits - 1 || degLen > degDigits) return null;
  const deg = parseInt(field.slice(0, degLen), 10);
  const min = parseFloat(field.slice(degLen));
  if (!Number.isFinite(deg) || !Number.isFinite(min)) return null;
  let value = deg + min / 60;
  if (hemi === 'S' || hemi === 'W') value = -value;
  return value;
}

// -----------------------------------------------------------------------------
// Time
// -----------------------------------------------------------------------------

function encodeTime(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}${mm}${ss}`;
}

function encodeDate(d: Date): string {
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yy = String(d.getUTCFullYear() % 100).padStart(2, '0');
  return `${dd}${mm}${yy}`;
}

/** Reassemble a UTC Date from an RMC time (hhmmss[.sss]) and date (ddmmyy). */
function parseDateTime(time: string, date: string): Date | null {
  if (time.length < 6 || date.length < 6) return null;
  const hh = parseInt(time.slice(0, 2), 10);
  const mm = parseInt(time.slice(2, 4), 10);
  const ss = parseFloat(time.slice(4));
  const dd = parseInt(date.slice(0, 2), 10);
  const mon = parseInt(date.slice(2, 4), 10);
  const yy = parseInt(date.slice(4, 6), 10);
  if ([hh, mm, ss, dd, mon, yy].some((n) => !Number.isFinite(n))) return null;
  // Two-digit year: 70-99 -> 1900s, 00-69 -> 2000s (standard NMEA reading).
  const year = yy >= 70 ? 1900 + yy : 2000 + yy;
  const whole = Math.trunc(ss);
  const ms = Math.round((ss - whole) * 1000);
  return new Date(Date.UTC(year, mon - 1, dd, hh, mm, whole, ms));
}

// -----------------------------------------------------------------------------
// Encode
// -----------------------------------------------------------------------------

export interface EncodeRmcInput {
  position: [number, number];
  sogKn: number;
  cogDeg: number;
  utc: Date;
  /** GPS talker id; 'GP' for GPS, 'GN' for multi-constellation. Default GP. */
  talker?: string;
}

/** Build an RMC sentence (recommended minimum). */
export function encodeRMC(input: EncodeRmcInput): string {
  const [lon, lat] = input.position;
  const la = encodeLat(lat);
  const lo = encodeLon(lon);
  const talker = input.talker ?? 'GP';
  const body = [
    `${talker}RMC`,
    encodeTime(input.utc),
    'A', // status: active/valid
    la.field,
    la.hemi,
    lo.field,
    lo.hemi,
    input.sogKn.toFixed(1),
    input.cogDeg.toFixed(1),
    encodeDate(input.utc),
    '', // magnetic variation, we work in true throughout
    '', // variation E/W
  ].join(',');
  return frame(body);
}

export interface EncodeGgaInput {
  position: [number, number];
  utc: Date;
  fixQuality?: number;
  satellites?: number;
  hdop?: number;
  /** Antenna altitude, metres. Default 0. */
  altitudeM?: number;
  talker?: string;
}

/** Build a GGA sentence (fix data with quality fields). */
export function encodeGGA(input: EncodeGgaInput): string {
  const [lon, lat] = input.position;
  const la = encodeLat(lat);
  const lo = encodeLon(lon);
  const talker = input.talker ?? 'GP';
  const body = [
    `${talker}GGA`,
    encodeTime(input.utc),
    la.field,
    la.hemi,
    lo.field,
    lo.hemi,
    String(input.fixQuality ?? 1),
    input.satellites != null ? String(input.satellites).padStart(2, '0') : '',
    (input.hdop ?? 0.9).toFixed(1),
    (input.altitudeM ?? 0).toFixed(1),
    'M',
    '0.0', // geoid separation
    'M',
    '', // age of DGPS data
    '', // DGPS station id
  ].join(',');
  return frame(body);
}

export interface EncodeGstInput {
  utc: Date;
  /** RMS of the standard deviation of range inputs, metres. */
  rmsM: number;
  semiMajorM: number;
  semiMinorM: number;
  orientDeg: number;
  latErrM: number;
  lonErrM: number;
  altErrM: number;
  talker?: string;
}

/** Build a GST sentence (GNSS pseudorange error statistics). */
export function encodeGST(input: EncodeGstInput): string {
  const talker = input.talker ?? 'GP';
  const body = [
    `${talker}GST`,
    encodeTime(input.utc),
    input.rmsM.toFixed(1),
    input.semiMajorM.toFixed(1),
    input.semiMinorM.toFixed(1),
    input.orientDeg.toFixed(1),
    input.latErrM.toFixed(1),
    input.lonErrM.toFixed(1),
    input.altErrM.toFixed(1),
  ].join(',');
  return frame(body);
}

export interface EncodeDepthInput {
  /** Depth below the transducer, metres. */
  depthM: number;
  /** Transducer offset, metres (DPT only). Default 0. */
  offsetM?: number;
  /** Talker id; depth sounders use 'SD' (sounder). Default SD. */
  talker?: string;
}

/** Build a DPT sentence (depth below transducer + offset). */
export function encodeDPT(input: EncodeDepthInput): string {
  const talker = input.talker ?? 'SD';
  const body = [`${talker}DPT`, input.depthM.toFixed(1), (input.offsetM ?? 0).toFixed(1)].join(',');
  return frame(body);
}

/** Build a DBT sentence (depth below transducer in feet, metres, fathoms). */
export function encodeDBT(input: EncodeDepthInput): string {
  const talker = input.talker ?? 'SD';
  const feet = input.depthM / 0.3048;
  const fathoms = input.depthM / 1.8288;
  const body = [
    `${talker}DBT`,
    feet.toFixed(1),
    'f',
    input.depthM.toFixed(1),
    'M',
    fathoms.toFixed(1),
    'F',
  ].join(',');
  return frame(body);
}

// -----------------------------------------------------------------------------
// Parse
// -----------------------------------------------------------------------------

/**
 * Parse a single NMEA line into a typed sentence, or null if it isn't a
 * sentence we handle, or the checksum doesn't match.
 *
 * We reject on a bad checksum rather than trust corrupted data, on a real
 * feed that's a garbled line we'd rather drop than plot.
 */
export function parseNmea(line: string): NmeaSentence | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('$')) return null;

  // Split off the checksum if present and verify it.
  const star = trimmed.indexOf('*');
  const body = star === -1 ? trimmed.slice(1) : trimmed.slice(1, star);
  if (star !== -1) {
    const given = trimmed
      .slice(star + 1)
      .trim()
      .toUpperCase();
    if (given !== nmeaChecksum(body)) return null;
  }

  const fields = body.split(',');
  const tag = fields[0]; // e.g. GPRMC, GNRMC, GPGGA
  const kind = tag.slice(2); // drop the 2-char talker id

  if (kind === 'RMC') {
    const time = fields[1] ?? '';
    const valid = (fields[2] ?? 'V') === 'A';
    const position = combine(
      parseCoord(fields[3] ?? '', fields[4] ?? '', 2),
      parseCoord(fields[5] ?? '', fields[6] ?? '', 3),
    );
    const sog = fields[7] ? parseFloat(fields[7]) : NaN;
    const cog = fields[8] ? parseFloat(fields[8]) : NaN;
    const date = fields[9] ?? '';
    return {
      type: 'RMC',
      valid,
      position,
      sogKn: Number.isFinite(sog) ? sog : null,
      cogDeg: Number.isFinite(cog) ? cog : null,
      utc: parseDateTime(time, date),
    };
  }

  if (kind === 'GGA') {
    const position = combine(
      parseCoord(fields[2] ?? '', fields[3] ?? '', 2),
      parseCoord(fields[4] ?? '', fields[5] ?? '', 3),
    );
    const fixQuality = fields[6] ? parseInt(fields[6], 10) : 0;
    // An empty sat-count field means "not reported": keep it undefined rather
    // than inventing a 0, so nothing downstream shows a made-up count.
    const satellites = fields[7] ? parseInt(fields[7], 10) : NaN;
    const hdop = fields[8] ? parseFloat(fields[8]) : NaN;
    return {
      type: 'GGA',
      position,
      fixQuality: Number.isFinite(fixQuality) ? fixQuality : 0,
      satellites: Number.isFinite(satellites) ? satellites : undefined,
      hdop: Number.isFinite(hdop) ? hdop : 0,
    };
  }

  if (kind === 'GST') {
    const n = (i: number) => {
      const v = fields[i] ? parseFloat(fields[i]) : NaN;
      return Number.isFinite(v) ? v : 0;
    };
    return {
      type: 'GST',
      rmsM: n(2),
      semiMajorM: n(3),
      semiMinorM: n(4),
      orientDeg: n(5),
      latErrM: n(6),
      lonErrM: n(7),
      altErrM: n(8),
    };
  }

  if (kind === 'DPT') {
    const depth = fields[1] ? parseFloat(fields[1]) : NaN;
    const offset = fields[2] ? parseFloat(fields[2]) : NaN;
    if (!Number.isFinite(depth)) return null;
    return { type: 'DPT', depthM: depth, offsetM: Number.isFinite(offset) ? offset : null };
  }

  if (kind === 'DBT') {
    // Prefer the metres field (index 3); fall back to feet (index 1).
    const metres = fields[3] ? parseFloat(fields[3]) : NaN;
    if (Number.isFinite(metres)) return { type: 'DBT', depthM: metres };
    const feet = fields[1] ? parseFloat(fields[1]) : NaN;
    if (Number.isFinite(feet)) return { type: 'DBT', depthM: feet * 0.3048 };
    return null;
  }

  if (kind === 'VHW') {
    // $--VHW,headingTrue,T,headingMag,M,speedKn,N,speedKmh,K
    const ht = fields[1] ? parseFloat(fields[1]) : NaN;
    const sk = fields[5] ? parseFloat(fields[5]) : NaN;
    if (!Number.isFinite(sk)) return null;
    return {
      type: 'VHW',
      headingTrueDeg: Number.isFinite(ht) ? ht : null,
      speedThroughWaterKn: sk,
    };
  }

  if (kind === 'MWV') {
    // $--MWV,angle,reference(R|T),speed,units(N|M|K),status(A|V)
    const angle = fields[1] ? parseFloat(fields[1]) : NaN;
    const ref = (fields[2] ?? 'R').toUpperCase();
    const speed = fields[3] ? parseFloat(fields[3]) : NaN;
    const units = (fields[4] ?? 'N').toUpperCase();
    const valid = (fields[5] ?? 'V').toUpperCase() === 'A';
    if (!Number.isFinite(angle) || !Number.isFinite(speed)) return null;
    return {
      type: 'MWV',
      windAngleDeg: ((angle % 360) + 360) % 360,
      reference: ref === 'T' ? 'T' : 'R',
      windSpeedKn: windSpeedToKn(speed, units),
      valid,
    };
  }

  if (kind === 'HDT') {
    // $--HDT,headingTrue,T
    const h = fields[1] ? parseFloat(fields[1]) : NaN;
    if (!Number.isFinite(h)) return null;
    return { type: 'HDT', headingTrueDeg: ((h % 360) + 360) % 360, headingMagneticDeg: null };
  }

  if (kind === 'HDG') {
    // $--HDG,headingMag,deviation,a(E|W),variation,a(E|W)
    const mag = fields[1] ? parseFloat(fields[1]) : NaN;
    if (!Number.isFinite(mag)) return null;
    const dev = fields[2] ? parseFloat(fields[2]) : NaN;
    const devSign = (fields[3] ?? '').toUpperCase() === 'W' ? -1 : 1;
    const varn = fields[4] ? parseFloat(fields[4]) : NaN;
    const varSign = (fields[5] ?? '').toUpperCase() === 'W' ? -1 : 1;
    // True = magnetic + variation (+ deviation), easterly positive.
    let trueDeg = mag;
    if (Number.isFinite(dev)) trueDeg += devSign * dev;
    if (Number.isFinite(varn)) trueDeg += varSign * varn;
    return {
      type: 'HDG',
      headingTrueDeg: ((trueDeg % 360) + 360) % 360,
      headingMagneticDeg: ((mag % 360) + 360) % 360,
    };
  }

  if (kind === 'MWD') {
    // $--MWD,dirTrue,T,dirMag,M,speedKn,N,speedMs,M
    const dirT = fields[1] ? parseFloat(fields[1]) : NaN;
    const spdKn = fields[5] ? parseFloat(fields[5]) : NaN;
    const spdMs = fields[7] ? parseFloat(fields[7]) : NaN;
    if (!Number.isFinite(dirT)) return null;
    const windSpeedKn = Number.isFinite(spdKn)
      ? spdKn
      : Number.isFinite(spdMs)
        ? spdMs * 1.943_844
        : NaN;
    if (!Number.isFinite(windSpeedKn)) return null;
    return { type: 'MWD', windFromDeg: ((dirT % 360) + 360) % 360, windSpeedKn };
  }

  return null;
}

/** Combine a parsed latitude and longitude into [lon, lat], or null. */
function combine(lat: number | null, lon: number | null): [number, number] | null {
  if (lat === null || lon === null) return null;
  return [lon, lat];
}

// -----------------------------------------------------------------------------
// Accuracy
// -----------------------------------------------------------------------------

/**
 * Horizontal position error (metres) from a GST sentence: the root-sum-square
 * of the latitude and longitude error standard deviations. This is the honest
 * "±X m" figure a receiver derives from its own solution.
 */
export function gstHorizontalErrorM(g: GstSentence): number {
  return Math.hypot(g.latErrM, g.lonErrM);
}

/**
 * Estimate horizontal error (metres) from GGA's HDOP when no GST is on the
 * feed. error = HDOP x UERE, with a nominal 4 m user-equivalent range error
 * for a modern consumer receiver. Rougher than GST, but it's what a GGA-only
 * receiver leaves us, so we're honest about where it comes from.
 */
export function estimateAccuracyFromHdop(hdop: number, uereM = 4): number {
  if (!Number.isFinite(hdop) || hdop <= 0) return uereM;
  return hdop * uereM;
}

// -----------------------------------------------------------------------------
// Depth
// -----------------------------------------------------------------------------

/**
 * The measured water depth (metres) from a parsed DPT or DBT. For DPT we add the
 * transducer offset when it's a positive (to-waterline) figure, so the value is
 * depth from the surface rather than from the transducer; DBT is the depth below
 * the transducer as-is. Returns null for any other sentence.
 */
export function measuredDepthM(sentence: NmeaSentence): number | null {
  if (sentence.type === 'DPT') {
    const offset = sentence.offsetM != null && sentence.offsetM > 0 ? sentence.offsetM : 0;
    return sentence.depthM + offset;
  }
  if (sentence.type === 'DBT') return sentence.depthM;
  return null;
}

// -----------------------------------------------------------------------------
// Instruments: speed, wind, heading
// -----------------------------------------------------------------------------

/** Convert a wind speed in the sentence's units (N=knots, M=m/s, K=km/h) to knots. */
function windSpeedToKn(value: number, units: string): number {
  switch (units.toUpperCase()) {
    case 'M':
      return value * 1.943_844; // m/s -> kn
    case 'K':
      return value / 1.852; // km/h -> kn
    default:
      return value; // N, already knots
  }
}

export interface EncodeVhwInput {
  /** Heading, degrees true. */
  headingTrueDeg: number;
  /** Speed through the water, knots. */
  speedThroughWaterKn: number;
  /** Instrument talker; speed logs use 'VW'. Default VW. */
  talker?: string;
}

/** Build a VHW sentence (water speed and heading). */
export function encodeVHW(input: EncodeVhwInput): string {
  const talker = input.talker ?? 'VW';
  const kmh = input.speedThroughWaterKn * 1.852;
  const body = [
    `${talker}VHW`,
    input.headingTrueDeg.toFixed(1),
    'T',
    '', // magnetic heading — we work in true
    'M',
    input.speedThroughWaterKn.toFixed(1),
    'N',
    kmh.toFixed(1),
    'K',
  ].join(',');
  return frame(body);
}

export interface EncodeMwvInput {
  /** Wind angle relative to the bow, degrees 0..360 (0 = ahead). */
  windAngleDeg: number;
  /** Wind speed, knots. */
  windSpeedKn: number;
  /** 'R' = apparent (relative), 'T' = true. Default R. */
  reference?: 'R' | 'T';
  /** Wind instruments use 'WI'. Default WI. */
  talker?: string;
}

/** Build an MWV sentence (wind speed and angle, apparent or true). */
export function encodeMWV(input: EncodeMwvInput): string {
  const talker = input.talker ?? 'WI';
  const body = [
    `${talker}MWV`,
    (((input.windAngleDeg % 360) + 360) % 360).toFixed(1),
    input.reference ?? 'R',
    input.windSpeedKn.toFixed(1),
    'N',
    'A',
  ].join(',');
  return frame(body);
}

export interface EncodeHdtInput {
  headingTrueDeg: number;
  /** Heading instruments use 'HE' (gyro) or 'HC' (compass). Default HE. */
  talker?: string;
}

/** Build an HDT sentence (true heading). */
export function encodeHDT(input: EncodeHdtInput): string {
  const talker = input.talker ?? 'HE';
  const body = [`${talker}HDT`, (((input.headingTrueDeg % 360) + 360) % 360).toFixed(1), 'T'].join(
    ',',
  );
  return frame(body);
}

export interface EncodeMwdInput {
  /** Direction the wind comes FROM, degrees true. */
  windFromDeg: number;
  /** Wind speed, knots. */
  windSpeedKn: number;
  talker?: string;
}

/** Build an MWD sentence (true wind direction and speed). */
export function encodeMWD(input: EncodeMwdInput): string {
  const talker = input.talker ?? 'WI';
  const ms = input.windSpeedKn / 1.943_844;
  const dir = (((input.windFromDeg % 360) + 360) % 360).toFixed(1);
  const body = [
    `${talker}MWD`,
    dir,
    'T',
    dir,
    'M',
    input.windSpeedKn.toFixed(1),
    'N',
    ms.toFixed(1),
    'M',
  ].join(',');
  return frame(body);
}

/** Speed through the water, or null, from any sentence that carries it (VHW). */
export function speedThroughWaterKn(sentence: NmeaSentence): number | null {
  return sentence.type === 'VHW' ? sentence.speedThroughWaterKn : null;
}

// -----------------------------------------------------------------------------
// True wind from apparent
// -----------------------------------------------------------------------------

export interface TrueWind {
  /** True wind speed, knots. */
  twsKn: number;
  /** True wind angle from the bow, −180..180 (+ = from starboard). */
  twaDeg: number;
  /** Direction the true wind comes FROM, degrees true. */
  windFromDeg: number;
}

/**
 * Derive the TRUE wind from the APPARENT wind the masthead unit feels, the boat's
 * speed through the water, and its heading. The instrument only ever measures
 * apparent wind — the blend of the real wind and the wind the boat makes by
 * moving — so to know the real wind we subtract the boat's motion.
 *
 * Working in a bow-relative frame (x ahead, y to starboard) with the wind as the
 * direction it comes FROM: the boat moving ahead at STW adds an apparent wind
 * from dead ahead of STW, so the true-wind FROM vector is the apparent-wind FROM
 * vector minus that headwind. Length is the true speed; its angle off the bow is
 * the true wind angle; add the heading to get the compass direction it's from.
 *
 * `awaDeg` is the apparent wind angle off the bow, 0..360 clockwise (0 = ahead).
 */
export function trueWindFromApparent(
  awaDeg: number,
  awsKn: number,
  stwKn: number,
  headingDeg: number,
): TrueWind {
  const a = (awaDeg * Math.PI) / 180;
  // Apparent-wind FROM vector minus the headwind the boat makes by moving ahead.
  const x = awsKn * Math.cos(a) - stwKn;
  const y = awsKn * Math.sin(a);
  const twsKn = Math.hypot(x, y);
  let twaDeg = (Math.atan2(y, x) * 180) / Math.PI; // −180..180 off the bow
  // Guard the degenerate all-zero case.
  if (!Number.isFinite(twaDeg)) twaDeg = 0;
  const windFromDeg = (((headingDeg + twaDeg) % 360) + 360) % 360;
  return {
    twsKn: Math.round(twsKn * 100) / 100,
    twaDeg: Math.round(twaDeg * 10) / 10,
    windFromDeg: Math.round(windFromDeg * 10) / 10,
  };
}
